/* eslint-disable max-statements */
/* eslint-disable curly, react/forbid-prop-types, object-property-newline, no-empty-function */
import { Alert, Button, Form } from "react-bootstrap";
import { CalendarIcon, ClockIcon, DesktopComputerIcon, EyeIcon, LocationMarkerIcon, TrashIcon } from "@heroicons/react/outline";
import { format, utcToZonedTime } from "date-fns-tz";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import MetaItem from "../MetaItem";

import { eventGuestsAllowed, isHybridEvent, isInPersonEvent, isRemoteEvent, registrationIsOpen } from "../../helpers/event";
import AddGuestButton from "./AddGuestButton";
import { getTimezone } from "../../helpers/dates";
import { useAuth } from "../../services/wp.auth.service";
import { useCourseRegistration } from "../../services/CourseRegistrationProvider";
import { userCanRegister } from "../../helpers/authHelpers";

const cellClasses = "py-4 align-middle";
const timeZone = getTimezone();

const CourseListItemRow = ({
	course,
	disabled,
	linkTitle,
	onChange,
	selectable,
	showRemoveButton
}) => {
	const { user } = useAuth();
	const {
		addCourse,
		guests,
		registeredCourses,
		registeredFor,
		removeCourse
	} = useCourseRegistration();

	const handleRemove = useCallback(() => {
		removeCourse(course);
	}, [course.id]);
	const canRemove = useMemo(() => registeredFor(course), [registeredCourses]);

	const handleCheckboxChange = useCallback(({ target }) => {
		if (onChange) {
			onChange({
				target,
				course
			});
		} else if (target.checked) {
			addCourse(course);
		} else {
			removeCourse(course);
		}
	}, [course, addCourse, removeCourse, onChange]);

	const canSelect = useMemo(() => registrationIsOpen(course) && userCanRegister(user) && !disabled, [course, disabled, user]);
	const colCount = useMemo(() => {
		let count = 2;
		if (selectable) count += 1;
		if (course.start) count += 2;
		if (showRemoveButton && canRemove) count += 1;
		return count;
	}, []);
	const isChecked = useMemo(() => registeredFor(course), [registeredCourses]);
	const inPerson = useMemo(() => isHybridEvent(course) ? !course.online : isInPersonEvent(course), [course]);
	const isOnline = useMemo(() => isHybridEvent(course) ? Boolean(course.online) : isRemoteEvent(course), [course]);
	const hybridEvent = useMemo(() => isHybridEvent(course), [course]);

	// Guest functions.
	const guestsAllowed = useMemo(() => !selectable && eventGuestsAllowed(course) && !onChange, [course, selectable, onChange]);

	return (
		<>
			<tr>
				{selectable ? (
					<td className={cellClasses}>
						{hybridEvent
							? <Link as={Button} to={`/class/${course.id}`} variant="primary"><EyeIcon className="i" /></Link>
							: (
								<Form.Group controlId="formBasicCheckbox">
									<Form.Check
										checked={isChecked}
										disabled={!canSelect}
										onChange={handleCheckboxChange}
										type="checkbox"
									/>
								</Form.Group>)}
					</td>) : null}

				<td className={cellClasses}>
					{linkTitle
						? <Link className="mb-2" to={`/class/${course.id}`}>{course.title}</Link>
						: <div className="mb-2">{course.title}</div>}
					{guestsAllowed
						? <AddGuestButton courseId={course.id} guests={guests[course.id]} />
						: null}
				</td>

				{course.start ? (
					<td className={cellClasses}>
						<MetaItem Icon={CalendarIcon} label={format(utcToZonedTime(course.start, timeZone), "MMMM dd, yyyy")} />
					</td>) : null}

				{course.start ? (
					<td className={cellClasses}>
						<MetaItem
							Icon={ClockIcon}
							label={`${format(utcToZonedTime(course.start, timeZone), "h:mm a")} - ${format(utcToZonedTime(course.end, timeZone), "h:mm a zzz", { timeZone })}`}
						/>
					</td>) : null}

				<td className={cellClasses}>
					{inPerson
						? (
							<MetaItem
								Icon={LocationMarkerIcon}
								label={typeof course.location === "string" ? course.location : course.location?.name || ""}
							/>)
						: null}
					{isOnline
						? <MetaItem Icon={DesktopComputerIcon} label="Online" />
						: null}
				</td>

				{ showRemoveButton && canRemove ? (
					<td className={cellClasses}>
						<Button
							onClick={handleRemove}
							size="sm"
							variant="danger"
						>
							<TrashIcon className="i" />
						</Button>
					</td>) : null}
			</tr>
			{course.error ? (
				<tr>
					<td colSpan={colCount}>
						<Alert className="m-0" variant="danger">
							{course.error}
						</Alert>
					</td>
				</tr>
			) : null}

			{course.notes ? (
				<tr>
					<td colSpan={colCount}>
						<Alert className="m-0" variant="info">
							{course.notes}
						</Alert>
					</td>
				</tr>
			) : null}
		</>
	);
};

CourseListItemRow.propTypes = {
	course: PropTypes.any.isRequired,
	disabled: PropTypes.bool,
	linkTitle: PropTypes.bool,
	onChange: PropTypes.func,
	selectable: PropTypes.bool,
	showRemoveButton: PropTypes.bool
};

CourseListItemRow.defaultProps = {
	disabled: false,
	linkTitle: false,
	onChange: null,
	selectable: false,
	showRemoveButton: true
};

export default CourseListItemRow;
